import { runtimeEnv } from 'config/runtimeEnv';
import requestInstance from './Request';

const BASE_URL = `${runtimeEnv('B2B_API_BASE_URL')}/admin-api/v1/tenant`;

// POST /admin-api/v1/tenant/provision — super-admin (role 99) only; the backend
// enforces this. Content-Type must be explicit: the request interceptor defaults
// POSTs without one to x-www-form-urlencoded. Two shapes:
//   plain object            → JSON
//   { ...fields, logoFile } → multipart (logo upload; object fields as JSON strings)
export const provisionTenantApi = async (payload) => {
  const { logoFile, ...fields } = payload || {};
  if (logoFile instanceof File) {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(
        key,
        typeof value === 'object' && value !== null ? JSON.stringify(value) : value,
      );
    });
    formData.append('logo', logoFile);
    const response = await requestInstance.post(`${BASE_URL}/provision`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }
  const response = await requestInstance.post(`${BASE_URL}/provision`, fields, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
};

// GET /admin-api/v1/tenant/service-cities — the platform-global city canon
// (master-data canon, D2 2026-08-23) for the Create Tenant city picker.
// Cities are picked from this list by id, never typed/created by the form.
export const getGlobalServiceCitiesApi = async () => {
  const response = await requestInstance.get(`${BASE_URL}/service-cities`);
  return response.data;
};

// ---------------------------------------------------------------------------
// Tenant onboarding DRAFTS (Phase 5b). Resumable, super-admin-owned
// work-in-progress for Create Tenant. A draft is not a tenant — only
// promoteDraftApi creates one. All endpoints are role-99 gated server-side.
// ---------------------------------------------------------------------------

// GET /tenant/drafts — the caller's resumable drafts ("Continue creating").
export const listTenantDraftsApi = async () => {
  const response = await requestInstance.get(`${BASE_URL}/drafts`);
  return response.data;
};

// POST /tenant/drafts — start a new empty draft ("Provision a new tenant").
export const createTenantDraftApi = async () => {
  const response = await requestInstance.post(
    `${BASE_URL}/drafts`,
    {},
    { headers: { 'Content-Type': 'application/json' } },
  );
  return response.data;
};

// GET /tenant/drafts/:id — full draft to resume the form.
export const getTenantDraftApi = async (id) => {
  const response = await requestInstance.get(`${BASE_URL}/drafts/${id}`);
  return response.data;
};

// PATCH /tenant/drafts/:id — LENIENT save ("Save & continue"). Accepts partial
// form state in any state; no schema is enforced until promote.
// payload = { payload: {...form}, branding: {...}, lastTabKey }
export const saveTenantDraftApi = async (id, body) => {
  const response = await requestInstance.patch(`${BASE_URL}/drafts/${id}`, body, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
};

// POST /tenant/drafts/:id/logo — stage a logo for an un-submitted draft; the
// object survives the session ending and is reused as the tenant logo on
// promote. Returns { key, name, url }.
export const uploadTenantDraftLogoApi = async (id, logoFile) => {
  const formData = new FormData();
  formData.append('logo', logoFile);
  const response = await requestInstance.post(`${BASE_URL}/drafts/${id}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// DELETE /tenant/drafts/:id — discard a draft (GCs any staged logo).
export const deleteTenantDraftApi = async (id) => {
  const response = await requestInstance.delete(`${BASE_URL}/drafts/${id}`);
  return response.data;
};

// POST /tenant/drafts/:id/promote — STRICT submit. On success creates the
// tenant (201). On validation failure returns 422 with a `fieldErrors` array
// ([{ field, tab, message }]) so the form can badge the tab + focus the field.
export const promoteTenantDraftApi = async (id) => {
  const response = await requestInstance
    .post(
      `${BASE_URL}/drafts/${id}/promote`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
      },
    )
    .catch((err) => {
      // Surface the structured 422 body to the caller instead of throwing, so
      // the form can drive tab/field navigation from fieldErrors.
      if (err.response?.status === 422) return { data: err.response.data };
      throw err;
    });
  return response.data;
};
